import type {
  ColumnType, FilterDef, FilterValue, FilterValues, Row,
} from './types';

export const defaultKeys = (s: { columns: { key: string; defaultVisible?: boolean }[] }) =>
  s.columns.filter((c) => c.defaultVisible).map((c) => c.key);

export const isEmpty = (v: FilterValue | undefined) => v === null || v === undefined || v === '';

export const selectFilter = (
  key: string,
  label: string,
  placeholder: string,
  options?: string[],
  field = key,
): FilterDef => ({
  key, field, label, type: 'select', placeholder,
  ...(options ? { options } : { runtimeOptions: true }),
});

/** Plain-text cell value, shared by the table and the CSV so the two can't drift. */
export function formatCell(
  value: string | number | null | undefined,
  type: ColumnType = 'text',
): string | number {
  if (value === null || value === undefined) return '';
  if (type === 'date') {
    const s = String(value);
    // SDK numeric timestamps (yyyyMMdd…) → yyyy-MM-dd
    if (/^\d{8}/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
    return s.slice(0, 10);
  }
  if (type === 'datetime') {
    const s = String(value);
    // SDK timestamps arrive as yyyyMMddHHmmss → "yyyy-MM-dd HH:mm"
    if (/^\d{14}$/.test(s)) {
      return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}`;
    }
    return s.replace('T', ' ').slice(0, 16);
  }
  return value;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const endOfDay = (d: Date) => startOfDay(d) + 24 * 60 * 60 * 1000 - 1;

/** True when the row passes every filter that has a value. Client-side sources only. */
export function rowMatches(row: Row, defs: FilterDef[], values: FilterValues): boolean {
  return defs.every((def) => {
    const v = values[def.key];
    if (isEmpty(v)) return true;
    const cell = row[def.field];

    if (def.type === 'select') return String(cell ?? '') === String(v);

    const time = new Date(String(cell)).getTime();
    const picked = v as Date;
    if (def.op === 'gte') return time >= (def.type === 'date' ? startOfDay(picked) : picked.getTime());
    return time <= (def.type === 'date' ? endOfDay(picked) : picked.getTime());
  });
}